import React from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import classNames from 'classnames';
import {
  Button,
  HStack,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { colors, spacing } from '../../../../App/ChakraTheme';

import styled from '../../../../consts/styled';
import ExcitedIcon from '../../../../components/icons/twitter_emoji/Excited';
import InspiredIcon from '../../../../components/icons/twitter_emoji/Inspired';
import MindblownIcon from '../../../../components/icons/twitter_emoji/Mindblown';
import NeutralIcon from '../../../../components/icons/twitter_emoji/Neutral';
import ConfusedIcon from '../../../../components/icons/twitter_emoji/Confused';
import BoredIcon from '../../../../components/icons/twitter_emoji/Bored';
import EasyIcon from '../../../../components/icons/twitter_emoji/Easy';
import OkIcon from '../../../../components/icons/twitter_emoji/Ok';
import HardIcon from '../../../../components/icons/twitter_emoji/Hard';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../../hooks/isMobile';

export const ModalBody = styled.div`
  background: white;
  border-radius: 8px;
  padding: 32px;
  padding-top: 16px;

  min-width: 700px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  transition: all 250ms ease-in-out;

  @media (max-width: 764px) {
    min-width: unset;
    padding: 16px;
  }

  h4:not(:first-child) {
    margin-top: ${spacing.BASE_PLUS};
    margin-bottom: ${spacing.SMALL};
  }
  h4 {
    margin-bottom: ${spacing.BASE};
  }
  h4 .red {
    color: ${colors.crimson};
  }
  .elaboration {
    width: calc(100% - 106px);
  }
  .elaboration textarea {
    min-width: 500px;
    min-height: 100px;
    resize: auto;

    @media (max-width: 764px) {
      min-width: unset;
    }
  }
  .row-cs.chakra-stack {
    width: fit-content;

    @media (max-width: 764px) {
      width: 100%;
      justify-content: space-between;
    }
  }
  .row-cs.chakra-stack .col-cs.chakra-stack {
    align-items: center;
    justify-items: center;
  }
  .col-cs.chakra-stack .caption-small {
    margin-top: 8px;
  }
  .impression {
    width: 72px;
    filter: grayscale(1);
    transition: all 250ms ease-in-out;

    @media (max-width: 764px) {
      width: 40px;
      margin-left: 0 !important;
    }
  }
  .impression:not(:first-child) {
    margin-left: 32px;

    @media (max-width: 764px) {
      margin-left: 8px;
    }
  }
  .impression .inspired {
    overflow: visible;
    transform: translateY(-21%);
  }
  .impression:hover,
  .impression:focus-visible,
  .impression.active:hover,
  .impression.active:focus-visible {
    cursor: pointer;
    filter: brightness(1.1) contrast(1.1);
    outline: 2px solid var(--stemi-focus-color);
    outline-offset: 4px;
    border-radius: 4px;
  }
  .impression:active,
  .impression:active:hover {
    filter: brightness(0.9) contrast(0.9);
  }
  .impression.active {
    filter: none;
  }

  .hr {
    height: 2px;
    background-color: ${colors.applyOpacity(colors.mischka, 0.5)};
    margin-top: ${spacing.BASE_PLUS};
    width: 100%;
  }
`;

interface FeedbackModalProps {
  selectedImpressions: string[];
  selectedDifficulty: string;
  elaboration?: string;
  setSelectedImpressions: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedDifficulty: React.Dispatch<React.SetStateAction<string>>;
  setElaboration: React.Dispatch<React.SetStateAction<string | undefined>>;
  send: () => void;
  isOpen: boolean;
}

const FeedbackModal: React.FC<FeedbackModalProps> = (props) => {
  const {
    selectedImpressions,
    selectedDifficulty,
    elaboration,
    setSelectedImpressions,
    setSelectedDifficulty,
    setElaboration,
    send,
    isOpen,
  } = props;

  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const impressions = [
    {
      name: t('feedback.impressions.excited'),
      icon: ExcitedIcon,
      value: 'excited',
    },
    {
      name: t('feedback.impressions.inspired'),
      icon: InspiredIcon,
      value: 'inspired',
    },
    {
      name: t('feedback.impressions.mindblown'),
      icon: MindblownIcon,
      value: 'mindblown',
    },
    {
      name: t('feedback.impressions.neutral'),
      icon: NeutralIcon,
      value: 'neutral',
    },
    {
      name: t('feedback.impressions.confused'),
      icon: ConfusedIcon,
      value: 'confused',
    },
    { name: t('feedback.impressions.bored'), icon: BoredIcon, value: 'bored' },
  ];

  const difficulty = [
    { name: t('feedback.difficulty.easy'), icon: EasyIcon, value: 'easy' },
    { name: t('feedback.difficulty.ok'), icon: OkIcon, value: 'ok' },
    { name: t('feedback.difficulty.hard'), icon: HardIcon, value: 'hard' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={() => {}}>
      <ModalOverlay />
      <ModalContent minW={isMobile ? '100%' : '700px'}>
        <ModalHeader></ModalHeader>
        <ModalBody>
          <Text id="impressions-heading" as="h4" textStyle="h4" fontSize={isMobile ? '14px' : '1.5rem'}>
            {t('feedback.afterCompletingIFeel')}
            <span className="red">*</span>
          </Text>
          <HStack role="radiogroup" aria-labelledby="impressions-heading" className="row-cs" spacing={0}>
            {impressions.map((f, index) => (
              <VStack
                role="radio"
                aria-checked={selectedImpressions.includes(f.value)}
                tabIndex={
                  selectedImpressions.includes(f.value) ||
                  (selectedImpressions.length === 0 && index === 0)
                    ? 0
                    : -1
                }
                className={classNames([
                  'col',
                  'impression',
                  { active: selectedImpressions.includes(f.value) },
                ])}
                key={f.value}
                onClick={() => setSelectedImpressions([f.value])}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    setSelectedImpressions([f.value]);
                  }
                  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    const next = impressions[(index + 1) % impressions.length];
                    setSelectedImpressions([next!.value]);
                    (e.currentTarget.parentElement?.children[
                      (index + 1) % impressions.length
                    ] as HTMLElement)?.focus();
                  }
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    const prev = impressions[(index - 1 + impressions.length) % impressions.length];
                    setSelectedImpressions([prev!.value]);
                    (e.currentTarget.parentElement?.children[
                      (index - 1 + impressions.length) % impressions.length
                    ] as HTMLElement)?.focus();
                  }
                }}
              >
                <f.icon
                  boxSize={isMobile ? '32px' : 'unset'}
                  className={f.value}
                />
                <Text
                  as="div"
                  fontSize={isMobile ? '10px !important' : 'inherit'}
                >
                  {f.name}
                </Text>
              </VStack>
            ))}
          </HStack>
          <Text id="difficulty-heading" as="h4" textStyle="h4" fontSize={isMobile ? '14px' : '1.5rem'}>
            {t('feedback.believeMeThisWas')}
            <span className="red">*</span>
          </Text>
          <HStack role="radiogroup" aria-labelledby="difficulty-heading" className="row-cs" spacing={0}>
            {difficulty.map((f, index) => (
              <VStack
                role="radio"
                aria-checked={selectedDifficulty === f.value}
                tabIndex={0}
                className={classNames([
                  'col',
                  'impression',
                  { active: selectedDifficulty === f.value },
                ])}
                key={f.value}
                onClick={() => setSelectedDifficulty(f.value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    setSelectedDifficulty(f.value);
                  }
                  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    const next = difficulty[(index + 1) % difficulty.length];
                    setSelectedDifficulty(next!.value);
                    (e.currentTarget.parentElement?.children[
                      (index + 1) % difficulty.length
                    ] as HTMLElement)?.focus();
                  }
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    const prev = difficulty[(index - 1 + difficulty.length) % difficulty.length];
                    setSelectedDifficulty(prev!.value);
                    (e.currentTarget.parentElement?.children[
                      (index - 1 + difficulty.length) % difficulty.length
                    ] as HTMLElement)?.focus();
                  }
                }}
                spacing={0}
              >
                <f.icon
                  boxSize={isMobile ? '32px' : 'unset'}
                  className={f.value}
                />
                <Text as="div" fontSize={isMobile ? '10px' : 'inherit'}>
                  {f.name}
                </Text>
              </VStack>
            ))}
          </HStack>
          <div className="hr"></div>
          <HStack
            className="row-cs"
            spacing={0}
            style={{ width: '100%', marginTop: spacing.BASE_PLUS }}
          >
            <Textarea
              placeholder={t('feedback.howCanWeMakeItBetter')}
              className="elaboration"
              onChange={(event: any) => setElaboration(event.target.value)}
              value={elaboration}
            />
            <Tippy
              disabled={
                !(
                  selectedDifficulty === '' || selectedImpressions.length === 0
                ) || undefined
              }
              arrow={true}
              theme="light"
              animation="scale"
              content={t('feedback.missingRequiredFields')}
            >
              {/* div for tippy, as events dont fire correctly when disabled */}
              <div>
                <Button
                  colorScheme="blue"
                  isDisabled={
                    selectedDifficulty === '' ||
                    selectedImpressions.length === 0
                  }
                  style={{ marginLeft: spacing.SMALL }}
                  onClick={() => {
                    send();
                  }}
                >
                  {t('feedback.send')}
                </Button>
              </div>
            </Tippy>
          </HStack>
        </ModalBody>
        <ModalFooter></ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FeedbackModal;
